/**
 * ชนิดข้อมูลของ Kanban/Pull (D-102) — เขียนตาม payload contract ที่ freeze แล้ว
 * (`apps/api/.../modules/kanban/schemas.py`, `docs/API_CONTRACT.md` — "Pull Contract")
 *
 * **สองโมดูลสิทธิ์ ไม่ใช่หนึ่ง** และนี่คือเหตุผลทั้งหมดที่ไฟล์นี้มีอยู่แยกจาก
 * `lib/mrp/types.ts` แม้ทั้งคู่จะอยู่ใน "การวางแผน/การผลิต" — RBAC เป็น `module ×
 * action` ดังนั้น "ตั้ง policy" กับ "สแกนเบิก" ซึ่งเป็น `CREATE` ทั้งคู่ แยกกันด้วย
 * action ไม่ได้ ต้องแยกกันด้วยโมดูล (D-102 ข้อ 1):
 *
 * - `KANBAN_CONFIG` — ตั้ง policy, ตัด control cycle, ออกการ์ด (ทั้งสามคือการตัดสิน
 *   ว่าไลน์นี้มี **กำลังการผลิต** เท่าไร ซึ่งเป็นสิ่งที่ operator ต้องแตะไม่ได้)
 * - `KANBAN_EXECUTION` — สแกนเบิก/รับทราบ/เริ่มเติม/เติมของ/ยกเลิก signal (ทั้งห้า
 *   คือการทำงานตามที่ policy วางไว้ ไม่ใช่การเปลี่ยน policy)
 *
 * ทั้งสองโมดูลไม่มีวันปรากฏบน route เดียวกัน — สองหน้าจอ (`ReplenishmentPolicyScreen`,
 * `KanbanSignalScreen`) จึงต้องแยกกันเหมือนที่สองโมดูลแยกกัน ไม่ใช่รวมเป็นจอเดียว
 * (`desktop/kanban/*Screen.tsx` มีเทสต์พิสูจน์ว่าสิทธิ์โมดูลหนึ่งเปิดอีกจอไม่ได้)
 */

/** โมดูลของหน้าจอ "ตั้งค่า Kanban" — ตั้ง policy, control cycle, ออกการ์ด (D-102 ข้อ 1) */
export const KANBAN_CONFIG_MODULE = 'KANBAN_CONFIG';

/** โมดูลของหน้าจอ "บอร์ด Kanban" — signal lifecycle ทั้งห้า transition (D-102 ข้อ 1) */
export const KANBAN_EXECUTION_MODULE = 'KANBAN_EXECUTION';

/* ------------------------------------------------------- replenishment_policy - */

export const PUSH_MODE = 'PUSH';
export const PULL_MODE = 'PULL';
export const REPLENISHMENT_POLICY_MODES = [PUSH_MODE, PULL_MODE] as const;
export type ReplenishmentPolicyMode = (typeof REPLENISHMENT_POLICY_MODES)[number];

export const DRAFT_POLICY_STATUS = 'DRAFT';
export const ACTIVE_POLICY_STATUS = 'ACTIVE';
export const SUPERSEDED_POLICY_STATUS = 'SUPERSEDED';
export const CANCELLED_POLICY_STATUS = 'CANCELLED';

/**
 * สี่สถานะของ policy ตามลำดับที่ contract เขียนไว้
 *
 * **ไม่มี route ใดเขียน `SUPERSEDED`** โดยตรง — มันเป็นผลข้างเคียงของการ `approve`
 * revision ถัดไป (D-102: "ในทรานแซกชันเดียวกัน policy เดิมกลายเป็น SUPERSEDED")
 * หน้าจอจึงต้องอ่านมันออกแต่ไม่มีปุ่มกระทำต่อมันเลย เหมือน `CONVERTED_TO_PO` ของ
 * ใบขอซื้อ
 */
export const REPLENISHMENT_POLICY_STATUSES = [
  DRAFT_POLICY_STATUS,
  ACTIVE_POLICY_STATUS,
  SUPERSEDED_POLICY_STATUS,
  CANCELLED_POLICY_STATUS,
] as const;
export type ReplenishmentPolicyStatus = (typeof REPLENISHMENT_POLICY_STATUSES)[number];

/* ------------------------------------------------------- kanban_control_cycle - */

export const ACTIVE_CYCLE_STATUS = 'ACTIVE';
export const SUPERSEDED_CYCLE_STATUS = 'SUPERSEDED';
export const CANCELLED_CYCLE_STATUS = 'CANCELLED';
export const KANBAN_CONTROL_CYCLE_STATUSES = [
  ACTIVE_CYCLE_STATUS,
  SUPERSEDED_CYCLE_STATUS,
  CANCELLED_CYCLE_STATUS,
] as const;
export type KanbanControlCycleStatus = (typeof KANBAN_CONTROL_CYCLE_STATUSES)[number];

/* ------------------------------------------------------------------ kanban_card - */

export const AVAILABLE_CARD_STATUS = 'AVAILABLE';
export const EMPTY_CARD_STATUS = 'EMPTY';
export const REPLENISHING_CARD_STATUS = 'REPLENISHING';
export const FULL_CARD_STATUS = 'FULL';
export const BLOCKED_CARD_STATUS = 'BLOCKED';
export const KANBAN_CARD_STATUSES = [
  AVAILABLE_CARD_STATUS,
  EMPTY_CARD_STATUS,
  REPLENISHING_CARD_STATUS,
  FULL_CARD_STATUS,
  BLOCKED_CARD_STATUS,
] as const;
export type KanbanCardStatus = (typeof KANBAN_CARD_STATUSES)[number];

/* -------------------------------------------------------------- kanban_signal - */

export const NEW_SIGNAL_STATUS = 'NEW';
export const ACKNOWLEDGED_SIGNAL_STATUS = 'ACKNOWLEDGED';
export const IN_REPLENISHMENT_SIGNAL_STATUS = 'IN_REPLENISHMENT';
export const REPLENISHED_SIGNAL_STATUS = 'REPLENISHED';
export const CANCELLED_SIGNAL_STATUS = 'CANCELLED';

/**
 * ห้าสถานะของ signal ตามลำดับที่ contract เขียนไว้
 *
 * **สัญญาณคือคำขอให้เติมของ ไม่ใช่การเคลื่อนสต๊อก** — มีแต่ `replenish` เท่านั้นที่
 * แตะ ledger จริง `empty`/`acknowledge`/`start-replenishment` ขยับแค่สถานะของ
 * signal เอง (ดูหมายเหตุยาวใน `KanbanSignalScreen.tsx`)
 */
export const KANBAN_SIGNAL_STATUSES = [
  NEW_SIGNAL_STATUS,
  ACKNOWLEDGED_SIGNAL_STATUS,
  IN_REPLENISHMENT_SIGNAL_STATUS,
  REPLENISHED_SIGNAL_STATUS,
  CANCELLED_SIGNAL_STATUS,
] as const;
export type KanbanSignalStatus = (typeof KANBAN_SIGNAL_STATUSES)[number];

/** สถานะที่ `cancel` ยังกระทำได้ — ตรงกับ `SIGNAL_OPEN_STATUSES` ฝั่งเซิร์ฟเวอร์ */
export const OPEN_SIGNAL_STATUSES: readonly KanbanSignalStatus[] = [
  NEW_SIGNAL_STATUS,
  ACKNOWLEDGED_SIGNAL_STATUS,
  IN_REPLENISHMENT_SIGNAL_STATUS,
];

export const PRODUCTION_ORDER_SUPPLY_DOCUMENT = 'PRODUCTION_ORDER';
export const STOCK_TRANSFER_SUPPLY_DOCUMENT = 'STOCK_TRANSFER';
export const SUPPLY_DOCUMENT_TYPES = [
  PRODUCTION_ORDER_SUPPLY_DOCUMENT,
  STOCK_TRANSFER_SUPPLY_DOCUMENT,
] as const;
export type SupplyDocumentType = (typeof SUPPLY_DOCUMENT_TYPES)[number];

/* ------------------------------------------------------------- ความยาวช่อง ---- */

/** ตรงกับคอลัมน์จริงฝั่งเซิร์ฟเวอร์ (`modules/kanban/models.py`) */
export const NOTE_MAX_LENGTH = 255;
export const CARD_CODE_MAX_LENGTH = 40;
export const PLANT_CODE_MAX_LENGTH = 20;
export const PRODUCT_CODE_MAX_LENGTH = 40;
export const STORAGE_LOCATION_CODE_MAX_LENGTH = 20;
export const WORK_CENTER_CODE_MAX_LENGTH = 20;

/* ------------------------------------------------------------------ กฎธุรกิจ -- */

/**
 * `400` — body พาฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของไปด้วย — **ใช้ร่วมกันทั้ง policy และ
 * control cycle** (`router.reject_server_owned_fields` และ
 * `router.reject_server_owned_cycle_fields` ตอบ rule เดียวกันนี้ทั้งคู่ ไม่ใช่ความ
 * ผิดพลาด — ทั้งสองเป็น "ฟิลด์ที่ policy/cycle ไม่ให้ client ตั้งเอง")
 */
export const POLICY_FIELD_IS_SERVER_OWNED = 'POLICY_FIELD_IS_SERVER_OWNED';
/** `400` — body พาฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของของการ์ดไปด้วย */
export const CARD_FIELD_IS_SERVER_OWNED = 'CARD_FIELD_IS_SERVER_OWNED';
/** `400` — body พาฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของของ signal ไปด้วย */
export const SIGNAL_FIELD_IS_SERVER_OWNED = 'SIGNAL_FIELD_IS_SERVER_OWNED';

/** `403` — โรงงานที่กรอกหรือของ record ที่อ้างอยู่นอก `plant_scope` ของผู้เรียก (D-004) */
export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';

/** `422` — ไม่มีสินค้ารหัสนี้ */
export const PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND';
/** `422` — มีสินค้ารหัสนี้แต่ยังไม่ ACTIVE */
export const PRODUCT_NOT_ACTIVE = 'PRODUCT_NOT_ACTIVE';
/** `422` — ไม่มีที่เก็บรหัสนี้ในโรงงานที่ระบุ */
export const LOCATION_NOT_FOUND = 'LOCATION_NOT_FOUND';
/** `422` — ที่เก็บที่ระบุเป็นของโรงงานอื่น */
export const LOCATION_IN_ANOTHER_PLANT = 'LOCATION_IN_ANOTHER_PLANT';
/** `422` — ไม่มีศูนย์งานรหัสนี้ในโรงงานที่ระบุ */
export const WORK_CENTER_NOT_FOUND = 'WORK_CENTER_NOT_FOUND';
/** `422` — ศูนย์งานที่ระบุเป็นของโรงงานอื่น */
export const WORK_CENTER_IN_ANOTHER_PLANT = 'WORK_CENTER_IN_ANOTHER_PLANT';
/** `422` — ต้นทางและปลายทางเป็นที่เก็บเดียวกัน */
export const SOURCE_AND_DESTINATION_ARE_THE_SAME = 'SOURCE_AND_DESTINATION_ARE_THE_SAME';
/** `422` — `effective_to` มาก่อนหรือเท่ากับ `effective_from` */
export const EFFECTIVE_RANGE_IS_NOT_ORDERED = 'EFFECTIVE_RANGE_IS_NOT_ORDERED';
/** `409` — flow นี้ (สินค้า+โรงงาน+ต้นทาง+ปลายทาง) มี policy ที่ ACTIVE อยู่แล้ว */
export const FLOW_ALREADY_HAS_AN_ACTIVE_POLICY = 'FLOW_ALREADY_HAS_AN_ACTIVE_POLICY';
/** `409` — สถานะของ policy ไม่ตรงกับที่หน้าจอเห็น หรือไม่มีเส้นทางจากสถานะนั้น */
export const POLICY_STATUS_CONFLICT = 'POLICY_STATUS_CONFLICT';
/**
 * `422` — ผู้อนุมัติต้องไม่ใช่ผู้สร้าง (D-010) **ไม่ใช่ `403`**: สิทธิ์ครบ แต่ห้าม
 * อนุมัติ policy ใบนี้ใบเดียว
 */
export const APPROVER_MUST_DIFFER_FROM_CREATOR = 'APPROVER_MUST_DIFFER_FROM_CREATOR';
/** `409` — ตัด control cycle บน policy ที่ยังไม่ ACTIVE ไม่ได้ */
export const POLICY_IS_NOT_ACTIVE = 'POLICY_IS_NOT_ACTIVE';
/** `409` — ออกการ์ดบน control cycle ที่ไม่ใช่ ACTIVE ไม่ได้ */
export const CYCLE_IS_NOT_ACTIVE = 'CYCLE_IS_NOT_ACTIVE';
/** `422` — จำนวนการ์ดที่หมุนเวียนอยู่จะเกิน `card_count` ที่ cycle ประกาศไว้ */
export const CYCLE_CARD_COUNT_EXCEEDED = 'CYCLE_CARD_COUNT_EXCEEDED';
/** `409` — `card_code` นี้ถูกใช้ไปแล้วที่ไหนสักแห่งในระบบ (unique ทั้งระบบ) */
export const CARD_CODE_ALREADY_ISSUED = 'CARD_CODE_ALREADY_ISSUED';

/** `422` — ไม่มีการ์ดรหัสนี้ในทะเบียน (คำตอบเรื่อง body จึงไม่ใช่ 404) */
export const CARD_NOT_FOUND = 'CARD_NOT_FOUND';
/** `409` — การ์ดใบนี้มี signal ที่ยังเปิดอยู่แล้ว (D-102 headline: หนึ่งการ์ดหนึ่ง signal) */
export const CARD_ALREADY_HAS_AN_OPEN_SIGNAL = 'CARD_ALREADY_HAS_AN_OPEN_SIGNAL';
/** `409` — สถานะปัจจุบันของการ์ดสแกนเบิกไม่ได้ (เช่น `BLOCKED`) */
export const CARD_CANNOT_SIGNAL = 'CARD_CANNOT_SIGNAL';
/**
 * `422` — ชิ้นนี้ยังมี open Push supply (Production Order/PR จาก MRP หรือ MPS)
 * อยู่ — D-102 ข้อ 8b: ระบบเดียวเติมของชิ้นหนึ่งได้ในเวลาหนึ่ง
 */
export const PART_ALREADY_COVERED_BY_PUSH_SUPPLY = 'PART_ALREADY_COVERED_BY_PUSH_SUPPLY';
/**
 * `409` — "แถวขยับใต้มือคุณ" ทั้ง `expected_status` ไม่ตรง และสถานะปัจจุบันไม่มี
 * เส้นทางไปยัง transition ที่ขอ — **ลองใหม่มีโอกาสสำเร็จ** (docs/API_CONTRACT.md,
 * ตัดสินตอนตรวจรับ `WO-P2-M7-005`)
 */
export const SIGNAL_STATUS_CONFLICT = 'SIGNAL_STATUS_CONFLICT';
/**
 * `422` — signal นี้จบไปแล้ว (`REPLENISHED` หรือ `CANCELLED`) ยกเลิกไม่ได้อีก —
 * **ต่างจาก `SIGNAL_STATUS_CONFLICT` โดยตั้งใจ**: นี่ไม่ใช่การแพ้ race และลองใหม่
 * ไม่มีทางสำเร็จ (docs/API_CONTRACT.md)
 */
export const SIGNAL_IS_NOT_OPEN = 'SIGNAL_IS_NOT_OPEN';
/** `409` — `client_event_id` นี้เคยใช้ไปแล้วกับ card_code อื่น (replay ชนกัน) */
export const CLIENT_EVENT_ID_ALREADY_USED = 'CLIENT_EVENT_ID_ALREADY_USED';
/** `422` — lot ที่ระบุในการเติมของไม่มีอยู่จริง */
export const LOT_NOT_FOUND = 'LOT_NOT_FOUND';
/** `422` — lot ที่ระบุเป็นของโรงงานอื่น ไม่ใช่โรงงานของ loop นี้ */
export const LOT_IN_ANOTHER_PLANT = 'LOT_IN_ANOTHER_PLANT';
/** `422` — lot ที่ระบุไม่ใช่สินค้าตัวเดียวกับที่ loop นี้เติม */
export const LOT_IS_NOT_THE_CARD_PRODUCT = 'LOT_IS_NOT_THE_CARD_PRODUCT';
/** `422` — ระบุ lot เดียวกันซ้ำสองบรรทัดในการเติมของครั้งเดียว */
export const DUPLICATE_LOT_IN_CONTAINER = 'DUPLICATE_LOT_IN_CONTAINER';

/* ------------------------------------------------------------- resource ---- */

/**
 * Policy หนึ่งใบตามที่ contract ระบุ (D-102 ข้อ 2)
 *
 * `container_qty` เป็น **สตริง** — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double
 * เก็บได้ครบ ส่วน `card_count` เป็นจำนวนเต็มจริง ๆ (ไม่ใช่ปริมาณต่อเนื่อง)
 *
 * `created_by_employee_id` ถูกส่งมาเสมอ — **หน้าจอต้องแสดงมัน** เพื่อให้ผู้อนุมัติ
 * เห็นว่าใบนี้เป็นของตัวเองหรือไม่ก่อนกด (D-010, ดู `ReplenishmentPolicyScreen.tsx`)
 */
export interface ReplenishmentPolicy {
  readonly policy_id: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly source_location_id: string;
  readonly source_location_code: string;
  readonly destination_location_id: string;
  readonly destination_location_code: string;
  readonly upstream_work_center_id: string | null;
  readonly upstream_work_center_code: string | null;
  readonly downstream_work_center_id: string | null;
  readonly downstream_work_center_code: string | null;
  readonly mode: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — ห้ามแปลงเป็น number */
  readonly container_qty: string;
  readonly card_count: number;
  readonly status: string;
  /** `YYYY-MM-DD` — เป็น `date` ไม่ใช่ `datetime` ฝั่งเซิร์ฟเวอร์ ไม่มีเขตเวลาติดมา */
  readonly effective_from: string;
  readonly effective_to: string | null;
  readonly revision_no: number;
  readonly superseded_by_policy_id: string | null;
  readonly created_by_employee_id: string;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
  readonly note: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/replenishment-policies` — envelope มาตรฐานของทุกกลุ่ม */
export interface ReplenishmentPolicyListPage {
  readonly items: readonly ReplenishmentPolicy[];
  readonly next_cursor: string | null;
}

/** Control cycle หนึ่งรอบตามที่ contract ระบุ (D-102 ข้อ 3) — ไม่มีผู้สร้าง/ผู้อนุมัติ */
export interface KanbanControlCycle {
  readonly cycle_id: string;
  readonly policy_id: string;
  readonly card_count: number;
  readonly status: string;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface KanbanControlCycleListPage {
  readonly items: readonly KanbanControlCycle[];
}

/** การ์ดหนึ่งใบที่ออกบน control cycle (D-102 ข้อ 4, 7b) */
export interface KanbanCard {
  readonly card_id: string;
  readonly cycle_id: string;
  readonly card_code: string;
  readonly tag_schema_version: number;
  readonly status: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/** หนึ่ง lot ที่ถูกใช้เติมภาชนะ (D-102 ข้อ 6) */
export interface KanbanContainerLot {
  readonly lot_id: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ */
  readonly qty: string;
  readonly created_at: string;
}

/**
 * Signal หนึ่งตัวพร้อมการ์ด/loop/โรงงานที่ resolve มาให้แล้ว
 *
 * `cancelled_by_employee_id`/`cancellation_reason`/`cancelled_at` เป็นสามค่าที่
 * มาด้วยกันเสมอเมื่อ `status === 'CANCELLED'` และเป็น `null` ทั้งสามเมื่อไม่ใช่
 * (`ck_kanban_signal_cancellation_is_whole` ฝั่งเซิร์ฟเวอร์)
 */
export interface KanbanSignal {
  readonly signal_id: string;
  readonly card_id: string;
  readonly card_code: string;
  readonly card_status: string;
  readonly cycle_id: string;
  readonly policy_id: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly status: string;
  readonly raised_by_employee_id: string;
  readonly client_event_id: string | null;
  readonly device_id: string | null;
  readonly device_sequence_no: number | null;
  readonly supply_document_type: string | null;
  readonly supply_document_id: string | null;
  readonly cancelled_by_employee_id: string | null;
  readonly cancellation_reason: string | null;
  readonly cancelled_at: string | null;
  readonly container_lots: readonly KanbanContainerLot[];
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/kanban-signals` — envelope มาตรฐานของทุกกลุ่ม */
export interface KanbanSignalListPage {
  readonly items: readonly KanbanSignal[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests --- */

/**
 * body ของ `POST /v1/replenishment-policies` — เกิดเป็น `DRAFT` เสมอ ไม่มีช่องให้
 * เลือกสถานะอื่น (D-102: การอนุมัติเป็นสิทธิ์คนละใบ)
 */
export interface CreateReplenishmentPolicyBody {
  readonly plant_code: string;
  readonly product_code: string;
  readonly source_location_code: string;
  readonly destination_location_code: string;
  readonly upstream_work_center_code?: string;
  readonly downstream_work_center_code?: string;
  readonly mode: ReplenishmentPolicyMode;
  /** สตริงเสมอ — ดูเหตุผลที่ `ReplenishmentPolicy.container_qty` */
  readonly container_qty: string;
  readonly card_count: number;
  /** `YYYY-MM-DD` — วันปฏิทิน ไม่ใช่ instant */
  readonly effective_from: string;
  readonly effective_to?: string;
  readonly note?: string;
}

/** body ของ `approve`/`cancel` ของ policy — ทั้งก้อนเป็น optional */
export interface PolicyTransitionBody {
  readonly expected_status?: string;
}

/** body ของ `POST .../control-cycles` — จำนวนการ์ดที่หมุนเวียนจริงเท่านั้น */
export interface CreateControlCycleBody {
  readonly card_count: number;
}

/** body ของ `POST .../control-cycles/{cycle_id}/cards` — รหัสที่พิมพ์บนป้ายเท่านั้น */
export interface IssueCardBody {
  readonly card_code: string;
}

/** body ของ `POST /v1/kanban-signals/empty` */
export interface ScanKanbanCardEmptyBody {
  readonly card_code: string;
  readonly client_event_id?: string;
}

/** body ของ `acknowledge` — ทั้งก้อนเป็น optional */
export interface KanbanSignalTransitionBody {
  readonly expected_status?: string;
}

/** body ของ `start-replenishment` — เอกสารที่กำลังเติมของเป็นข้อมูลบังคับ */
export interface StartKanbanReplenishmentBody {
  readonly supply_document_type: SupplyDocumentType;
  readonly supply_document_id: string;
  readonly expected_status?: string;
}

/** body ของ `cancel` ของ signal — เหตุผลเป็นข้อมูลบังคับ (WO-P2-M7-005) */
export interface CancelKanbanSignalBody {
  readonly cancellation_reason: string;
  readonly expected_status?: string;
}

/** หนึ่งบรรทัดของการเติมของ — lot และปริมาณที่ใช้เติม */
export interface KanbanContainerLotInput {
  readonly lot_id: string;
  /** สตริงเสมอ — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754 double เก็บได้ครบ */
  readonly qty: string;
}

/** body ของ `replenish` — อย่างน้อยหนึ่ง lot (D-102 ข้อ 6: หลาย lot ต่อภาชนะได้) */
export interface ReplenishKanbanSignalBody {
  readonly lots: readonly KanbanContainerLotInput[];
  readonly expected_status?: string;
}
